import { Upload } from "lucide-react";
import { Suspense } from "react";
import { useTranslation } from "react-i18next";
import { Link } from "react-router";

import { PanelLoading } from "~/components/loading";
import { Button } from "~/components/ui";
import type { CompoundRecordComposerSearchCondition } from "~/features/compoundRecordComposers/types";
import { CompoundRecordComposerPeek } from "./CompoundRecordComposerPeek";
import { CompoundRecordComposersSearchControls } from "./CompoundRecordComposersSearchControls";
import { CompoundRecordComposersTable } from "./CompoundRecordComposersTable";

interface CompoundRecordComposersPresenterProps {
  searchConditions: CompoundRecordComposerSearchCondition[];
  onConditionsChange: (next: CompoundRecordComposerSearchCondition[]) => void;
  onPageReset: () => void;
  pageIndex: number;
  onPageIndexChange: (next: number) => void;
  pageSize: number;
  onPageSizeChange: (next: number) => void;
  selectedComposer: string | null;
  onSelect: (id: string) => void;
  onCloseSelected: () => void;
}

export function CompoundRecordComposersPresenter({
  searchConditions,
  onConditionsChange,
  onPageReset,
  pageIndex,
  onPageIndexChange,
  pageSize,
  onPageSizeChange,
  selectedComposer,
  onSelect,
  onCloseSelected,
}: CompoundRecordComposersPresenterProps) {
  const { t } = useTranslation();

  return (
    <div className="mx-auto max-w-[var(--content-max)] space-y-5 p-8">
      <div className="flex flex-wrap items-end justify-between gap-4">
        <h1 className="text-dewey-title capitalize">{t("model.compoundRecordComposer.list")}</h1>
        <Button type="button" variant="outline" size="sm" asChild>
          <Link to="upload">
            <Upload />
            <span>{t("feature.compoundRecordComposers.upload.csvUpload")}</span>
          </Link>
        </Button>
      </div>

      <CompoundRecordComposersSearchControls
        conditions={searchConditions}
        onConditionsChange={onConditionsChange}
        onPageReset={onPageReset}
      />

      <Suspense fallback={<PanelLoading />}>
        <CompoundRecordComposersTable
          searchConditions={searchConditions}
          pageIndex={pageIndex}
          onPageIndexChange={onPageIndexChange}
          pageSize={pageSize}
          onPageSizeChange={onPageSizeChange}
          onSelect={onSelect}
        />
      </Suspense>

      <CompoundRecordComposerPeek id={selectedComposer} onClose={onCloseSelected} />
    </div>
  );
}
